/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { IoCloseCircleSharp } from 'react-icons/io5'
import {
  FiMapPin,
  FiTrendingUp,
  FiDollarSign,
  FiPackage,
  FiTruck,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiShoppingBag,
  FiCheckCircle,
} from 'react-icons/fi'

export interface CityPdvProduct {
  code: string
  name: string
  qty: number
  value: number
}

export interface CityPdvData {
  cnpj: string
  name: string
  city: string
  bairro: string
  address: string
  cep: string
  uf: string
  distName: string
  distId?: string | number
  totalValue: number
  totalVolume: number
  transactionsCount: number
  skuCount: number
  products: CityPdvProduct[]
}

export interface CityMapPoint {
  city: string
  uf: string
  coordinates: [number, number]
  activePdvsCount: number
  totalValue: number
  totalVolume: number
  avgTicket: number
  avgSkusPerPdv: number
  uniqueSkusCount: number
  tier: 'top' | 'mid' | 'low'
  growthStatus: 'high_opportunity' | 'expanding' | 'consolidated'
  pdvs: CityPdvData[]
  zoneKey?: string
  zoneLabel?: string
  zoneColor?: string
  macroRegion?: 'Norte' | 'Centro' | 'Sul'
  population?: number
  potencialPdvs?: number
  potencialPct?: number
}

interface Props {
  cityData: CityMapPoint
  onClose: () => void
  isVolumeMode?: boolean
}

export const CityDetailsModal: React.FC<Props> = ({
  cityData,
  onClose,
  isVolumeMode = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedPdv, setExpandedPdv] = useState<string | null>(null)

  const toggleExpand = (cnpj: string) => {
    setExpandedPdv((prev) => (prev === cnpj ? null : cnpj))
  }

  // Filtragem de PDVs na busca interna
  const filteredPdvs = useMemo(() => {
    if (!searchTerm.trim()) return cityData.pdvs
    const term = searchTerm.toLowerCase().trim()
    return cityData.pdvs.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.cnpj.toLowerCase().includes(term) ||
        p.bairro.toLowerCase().includes(term) ||
        p.distName.toLowerCase().includes(term),
    )
  }, [cityData.pdvs, searchTerm])

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(val)
  }

  const formatVolume = (val: number) => {
    return `${val.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} CX`
  }

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Cabeçalho da Cidade */}
        <ModalHeader>
          <div className='city-title-group'>
            <div className='icon-wrap'>
              <FiMapPin size={24} color='#a6c10b' />
            </div>
            <div>
              <h3>
                {cityData.city} - {cityData.uf}
              </h3>
              <div className='meta-sub'>
                <span className='pdv-count-badge'>
                  {cityData.activePdvsCount}{' '}
                  {cityData.activePdvsCount === 1 ? 'PDV Atendido' : 'PDVs Atendidos'}
                </span>
                <span className='status-tag'>
                  {cityData.growthStatus === 'high_opportunity'
                    ? 'Alto Potencial de Expansão'
                    : cityData.growthStatus === 'expanding'
                      ? 'Potencial de Aumento de Mix'
                      : 'Mercado Consolidado'}
                </span>
              </div>
            </div>
          </div>

          <CloseButton onClick={onClose} type='button' title='Fechar'>
            <IoCloseCircleSharp size={28} />
          </CloseButton>
        </ModalHeader>

        {/* KPIs da Cidade */}
        <KpiGrid>
          {/* Card Potencial de Crescimento */}
          <KpiCard $highlight={true}>
            <div className='kpi-header'>
              <FiTrendingUp size={16} color='#a6c10b' />
              <span>Diagnóstico de Potencial</span>
            </div>
            <strong className='kpi-val'>
              {cityData.growthStatus === 'high_opportunity'
                ? 'Grande Oportunidade'
                : cityData.growthStatus === 'expanding'
                  ? 'Expansão de Mix'
                  : 'Alta Cobertura'}
            </strong>
            <span className='kpi-sub'>
              {cityData.growthStatus === 'high_opportunity'
                ? 'Poucos PDVs ativos. Alto espaço para abertura de novos clientes.'
                : cityData.growthStatus === 'expanding'
                  ? 'Boa base instalada. Oportunidade de positivar mais SKUs por PDV.'
                  : 'Excelente penetração e giro consolidado de produtos.'}
            </span>
          </KpiCard>

          {/* Card Média de Venda */}
          <KpiCard>
            <div className='kpi-header'>
              <FiDollarSign size={16} color='#38bdf8' />
              <span>{isVolumeMode ? 'Volume & Média' : 'Vendas & Média'}</span>
            </div>
            <strong className='kpi-val highlight'>
              {isVolumeMode
                ? formatVolume(cityData.totalVolume)
                : formatCurrency(cityData.totalValue)}
            </strong>
            <span className='kpi-sub'>
              Média por PDV:{' '}
              <strong style={{ color: '#e2e8f0' }}>
                {isVolumeMode
                  ? formatVolume(cityData.avgTicket)
                  : formatCurrency(cityData.avgTicket)}
              </strong>
            </span>
          </KpiCard>

          {/* Card Inteligência de SKUs */}
          <KpiCard>
            <div className='kpi-header'>
              <FiPackage size={16} color='#facc15' />
              <span>Métricas de SKUs</span>
            </div>
            <strong className='kpi-val'>
              {cityData.avgSkusPerPdv.toFixed(1)} <span style={{ fontSize: 13 }}>SKUs / PDV</span>
            </strong>
            <span className='kpi-sub'>
              {cityData.uniqueSkusCount} SKUs distintos movimentados no município
            </span>
          </KpiCard>

          {/* Card Habitantes & Potencial IBGE */}
          <KpiCard>
            <div className='kpi-header'>
              <FiCheckCircle
                size={16}
                color={(cityData.potencialPct || 0) >= 70 ? '#4ade80' : '#facc15'}
              />
              <span>Habitantes & Potencial IBGE</span>
            </div>
            <strong className='kpi-val'>
              {cityData.population ? `${cityData.population.toLocaleString('pt-BR')} hab` : '—'}
            </strong>
            <span className='kpi-sub'>
              Potencial: <strong>{cityData.potencialPdvs || 0} PDVs</strong> •{' '}
              <span
                style={{
                  color: (cityData.potencialPct || 0) >= 70 ? '#4ade80' : '#facc15',
                  fontWeight: 700,
                }}
              >
                {cityData.potencialPct || 0}%{' '}
                {(cityData.potencialPct || 0) >= 70 ? '(Meta Batida)' : '(Abaixo de 70%)'}
              </span>
            </span>
          </KpiCard>
        </KpiGrid>

        {/* Seção de PDVs com Busca */}
        <SectionHeader>
          <div className='title-area'>
            <h4>PDVs Atendidos em {cityData.city}</h4>
            <span className='subtitle'>
              Clique em um PDV para expandir o fornecedor, dados e mix de SKUs
            </span>
          </div>

          <SearchWrapper>
            <FiSearch size={14} color='#64748b' />
            <input
              type='text'
              placeholder='Buscar PDV por nome, CNPJ, fornecedor...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>
        </SectionHeader>

        {/* Lista Accordion de PDVs */}
        <PdvListContainer>
          {filteredPdvs.length === 0 ? (
            <EmptyState>Nenhum PDV encontrado para os critérios informados.</EmptyState>
          ) : (
            filteredPdvs.map((pdv) => {
              const isExpanded = expandedPdv === pdv.cnpj

              return (
                <PdvAccordionItem key={pdv.cnpj} $isExpanded={isExpanded}>
                  {/* Cabeçalho do Accordion (Clique para expandir) */}
                  <PdvHeader onClick={() => toggleExpand(pdv.cnpj)}>
                    <div className='pdv-main-info'>
                      <div className='pdv-status-icon'>
                        <FiCheckCircle size={16} color='#10b981' />
                      </div>
                      <div>
                        <span className='pdv-name'>{pdv.name}</span>
                        <div className='pdv-meta-row'>
                          <span className='pdv-cnpj'>CNPJ: {pdv.cnpj}</span>
                          {pdv.bairro && pdv.bairro !== 'Não informado' && (
                            <span className='pdv-bairro'>Bairro: {pdv.bairro}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='pdv-stats-right'>
                      <div className='stat-block'>
                        <span className='stat-label'>Média SKUs</span>
                        <span className='stat-val sku-badge'>
                          <FiPackage size={11} /> {pdv.skuCount} SKUs
                        </span>
                      </div>

                      <div className='stat-block' style={{ textAlign: 'right' }}>
                        <span className='stat-label'>
                          {isVolumeMode ? 'Volume Total' : 'Faturamento Total'}
                        </span>
                        <span className='stat-val highlight'>
                          {isVolumeMode
                            ? formatVolume(pdv.totalVolume)
                            : formatCurrency(pdv.totalValue)}
                        </span>
                      </div>

                      <div className='chevron-wrap'>
                        {isExpanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                      </div>
                    </div>
                  </PdvHeader>

                  {/* Detalhes Expansíveis do PDV */}
                  {isExpanded && (
                    <PdvExpandedContent>
                      {/* Grid de Informações do PDV */}
                      <PdvDetailsGrid>
                        <DetailCard>
                          <div className='detail-title'>
                            <FiTruck size={13} color='#a6c10b' /> Fornecedor / Distribuidor
                          </div>
                          <strong className='detail-value'>{pdv.distName}</strong>
                          {pdv.distId && (
                            <span className='detail-sub'>Cód. Distribuidor: {pdv.distId}</span>
                          )}
                        </DetailCard>

                        <DetailCard>
                          <div className='detail-title'>
                            <FiMapPin size={13} color='#facc15' /> Endereço Completo
                          </div>
                          <strong className='detail-value'>
                            {pdv.address || 'Não informado'}
                          </strong>
                          <span className='detail-sub'>
                            {pdv.bairro !== 'Não informado' ? `Bairro: ${pdv.bairro}` : ''}
                            {pdv.cep ? ` • CEP: ${pdv.cep}` : ''}
                          </span>
                        </DetailCard>

                        <DetailCard>
                          <div className='detail-title'>
                            <FiShoppingBag size={13} color='#38bdf8' /> Giro & Transações
                          </div>
                          <strong className='detail-value'>
                            {pdv.transactionsCount} transações registradas
                          </strong>
                          <span className='detail-sub'>
                            Giro médio:{' '}
                            {isVolumeMode
                              ? formatVolume(pdv.totalVolume / (pdv.transactionsCount || 1))
                              : formatCurrency(pdv.totalValue / (pdv.transactionsCount || 1))}{' '}
                            / pedido
                          </span>
                        </DetailCard>
                      </PdvDetailsGrid>

                      {/* Tabela de Produtos Comprados por esse PDV */}
                      {pdv.products && pdv.products.length > 0 && (
                        <ProductTableWrapper>
                          <div className='table-label'>
                            Mix de SKUs Comprados ({pdv.products.length}{' '}
                            {pdv.products.length === 1 ? 'item' : 'itens'} distintos):
                          </div>
                          <ProductTable>
                            <thead>
                              <tr>
                                <th>Produto</th>
                                <th style={{ textAlign: 'center' }}>Quantidade</th>
                                <th style={{ textAlign: 'right' }}>
                                  {isVolumeMode ? 'Volume (CX)' : 'Total (R$)'}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {pdv.products.map((p, idx) => (
                                <tr key={`${p.code}-${idx}`}>
                                  <td>
                                    <div className='prod-info'>
                                      <span className='prod-title'>{p.name}</span>
                                      <span className='prod-sku-code'>SKU: {p.code}</span>
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    <span className='qty-pill'>
                                      {p.qty.toLocaleString('pt-BR')}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <span className='val-pill'>
                                      {isVolumeMode
                                        ? `${p.qty.toLocaleString('pt-BR')} CX`
                                        : formatCurrency(p.value)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </ProductTable>
                        </ProductTableWrapper>
                      )}
                    </PdvExpandedContent>
                  )}
                </PdvAccordionItem>
              )
            })
          )}
        </PdvListContainer>
      </ModalContainer>
    </Overlay>
  )
}

// Styled Components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  padding: 20px;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const ModalContainer = styled.div`
  background: #0f172a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  width: 100%;
  max-width: 980px;
  height: 88vh;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes slideUp {
    from {
      transform: translateY(20px) scale(0.97);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  .city-title-group {
    display: flex;
    align-items: center;
    gap: 14px;

    .icon-wrap {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: rgba(166, 193, 11, 0.15);
      border: 1px solid rgba(166, 193, 11, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h3 {
      font-size: 19px;
      color: #ffffff;
      font-weight: 700;
      margin: 0;
    }

    .meta-sub {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 4px;
      flex-wrap: wrap;

      .pdv-count-badge {
        font-size: 11px;
        background: rgba(56, 189, 248, 0.15);
        color: #38bdf8;
        padding: 2px 8px;
        border-radius: 6px;
        font-weight: 600;
        border: 1px solid rgba(56, 189, 248, 0.3);
      }

      .status-tag {
        font-size: 11px;
        background: rgba(166, 193, 11, 0.15);
        color: #a6c10b;
        padding: 2px 8px;
        border-radius: 6px;
        font-weight: 600;
        border: 1px solid rgba(166, 193, 11, 0.3);
      }
    }
  }
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #ef4444;
  }
`

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  padding: 18px 24px 10px 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const KpiCard = styled.div<{ $highlight?: boolean }>`
  background: rgba(30, 41, 59, 0.45);
  border: 1px solid
    ${({ $highlight }) => ($highlight ? 'rgba(166, 193, 11, 0.3)' : 'rgba(255, 255, 255, 0.06)')};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .kpi-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #94a3b8;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .kpi-val {
    font-size: 18px;
    color: #ffffff;
    font-weight: 700;

    &.highlight {
      color: #38bdf8;
    }
  }

  .kpi-sub {
    font-size: 11px;
    color: #64748b;
    line-height: 1.4;
  }
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  gap: 14px;
  flex-wrap: wrap;

  .title-area {
    h4 {
      font-size: 14px;
      color: #ffffff;
      font-weight: 700;
      margin: 0;
    }

    .subtitle {
      font-size: 11px;
      color: #64748b;
    }
  }
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 6px 12px;
  border-radius: 8px;
  width: 320px;

  input {
    background: transparent;
    border: none;
    outline: none;
    color: #ffffff;
    font-size: 12px;
    width: 100%;

    &::placeholder {
      color: #64748b;
    }
  }
`

const PdvListContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
`

const PdvAccordionItem = styled.div<{ $isExpanded: boolean }>`
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  background: rgba(30, 41, 59, 0.45);
  border: 1px solid
    ${({ $isExpanded }) => ($isExpanded ? 'rgba(56, 189, 248, 0.45)' : 'rgba(255, 255, 255, 0.07)')};
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(56, 189, 248, 0.35);
    background: rgba(30, 41, 59, 0.6);
  }
`

const PdvHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  min-height: 56px;
  box-sizing: border-box;
  cursor: pointer;
  user-select: none;
  background: rgba(255, 255, 255, 0.015);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  .pdv-main-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;

    .pdv-status-icon {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .pdv-name {
      font-size: 13px;
      font-weight: 700;
      color: #f8fafc;
      display: block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 420px;
    }

    .pdv-meta-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 3px;
      font-size: 11px;
      color: #94a3b8;

      .pdv-cnpj {
        font-family: monospace;
        color: #64748b;
      }

      .pdv-bairro {
        color: #94a3b8;
      }
    }
  }

  .pdv-stats-right {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;

    .stat-block {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .stat-label {
        font-size: 10px;
        color: #64748b;
        text-transform: uppercase;
      }

      .stat-val {
        font-size: 12px;
        font-weight: 700;
        color: #e2e8f0;

        &.highlight {
          color: #38bdf8;
        }

        &.sku-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          background: rgba(250, 204, 21, 0.12);
          color: #facc15;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
        }
      }
    }

    .chevron-wrap {
      color: #64748b;
      display: flex;
      align-items: center;
    }
  }
`

const PdvExpandedContent = styled.div`
  padding: 14px 16px 18px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const PdvDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const DetailCard = styled.div`
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  .detail-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: #94a3b8;
    text-transform: uppercase;
    font-weight: 600;
  }

  .detail-value {
    font-size: 12px;
    color: #ffffff;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .detail-sub {
    font-size: 10px;
    color: #64748b;
  }
`

const ProductTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .table-label {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`

const ProductTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    text-align: left;
    padding: 8px 10px;
    color: #64748b;
    font-weight: 600;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    font-size: 10px;
    text-transform: uppercase;
  }

  td {
    padding: 8px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    color: #e2e8f0;
  }

  .prod-info {
    display: flex;
    flex-direction: column;

    .prod-title {
      font-weight: 600;
      color: #ffffff;
    }

    .prod-sku-code {
      font-size: 10px;
      color: #64748b;
      font-family: monospace;
    }
  }

  .qty-pill {
    background: rgba(255, 255, 255, 0.06);
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 11px;
  }

  .val-pill {
    font-weight: 700;
    color: #10b981;
    font-size: 11px;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 30px;
  color: #64748b;
  font-size: 13px;
`
